/*
 * Author: Harper Yan
 * File Created: Wednesday, 25-02-12 04:07 pm
 * Last Modified: Tuesday, 25-03-25 08:07 pm, by Harper Yan
 */

// https://github.com/bjoerge/debounce-promise/blob/180c2854f566af3ac1e958d31dbb8f39c966e712/index.js
// https://github.com/bjoerge/debounce-promise
export interface DebouncePromiseOptions {
  leading?: boolean;
  trailing?: boolean;
  accumulate?: boolean;
}

interface Deferred<T> {
  resolve: (value: T | PromiseLike<T>) => void;
  reject: (reason?: any) => void;
  promise: Promise<T>;
}

function defer<T>(): Deferred<T> {
  const deferred = {} as Deferred<T>;
  deferred.promise = new Promise<T>((resolve, reject) => {
    deferred.resolve = resolve;
    deferred.reject = reject;
  });
  return deferred;
}

function getWait(wait: number | (() => number) | undefined): number {
  return typeof wait === 'function' ? wait() : (wait ?? 0);
}

export function debouncePromise<T extends (...args: any[]) => any>(
  fn: T,
  wait?: number | (() => number),
  options?: DebouncePromiseOptions
): (...args: Parameters<T>) => Promise<ReturnType<T>> {
  let lastCallAt: number | undefined;
  let deferred: Deferred<any> | null = null;
  let firstPromise: Promise<any> | undefined;
  let timer: ReturnType<typeof setTimeout>;
  let pendingArgs: any[][] = [];

  const {
    leading = false,
    trailing = true,
    accumulate = false,
  } = options ?? {};

  return function debounced(this: any, ...args: Parameters<T>) {
    const currentWait = getWait(wait);
    const currentTime = new Date().getTime();

    const isCold = !lastCallAt || currentTime - lastCallAt > currentWait;

    lastCallAt = currentTime;

    if (isCold && leading) {
      firstPromise = accumulate
        ? Promise.resolve(fn.call(this, [args])).then((result) => result[0])
        : Promise.resolve(fn.call(this, ...args));
      return firstPromise;
    }

    if (deferred) {
      clearTimeout(timer);
    } else {
      deferred = defer<any>();
    }

    if (trailing) {
      pendingArgs.push(args);
      timer = setTimeout(flush.bind(this), currentWait);

      if (accumulate) {
        const argsIndex = pendingArgs.length - 1;
        return deferred.promise.then((results) => results[argsIndex]);
      }
    } else {
      return firstPromise as Promise<ReturnType<T>>;
    }

    return deferred.promise as Promise<ReturnType<T>>;
  };

  function flush(this: any) {
    const thisDeferred = deferred;
    clearTimeout(timer);

    if (!thisDeferred) return;

    Promise.resolve(
      accumulate
        ? fn.call(this, pendingArgs)
        : fn.apply(this, pendingArgs[pendingArgs.length - 1] || [])
    ).then(thisDeferred.resolve, thisDeferred.reject);

    pendingArgs = [];
    deferred = null;
  }
}

export const sleep = (ms: number): Promise<void> => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};
